/* Model personality — compact layout (option 1b). Section rail (Conversational
   style, Model information) with a filled-fields count instead of an Enabled
   toggle; each section is titled groups of 44px field rows. Every change saves
   on its own (debounced for typing, straight away for choices) — there is no
   Save button. Only the columns that changed are sent. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { Account } from '../../../../api/client';
import { useAuth } from '../../../../context/AuthContext';
import { isAccountOwner } from '../../../../permissions';
import { useAutosave } from '../../../../hooks/useAutosave';
import { CopyConfigModal } from '../../../CopyConfigModal';
import type { ChatStylePreset } from '../../chatStylePresets';
import { messageStyleErrors, messageStyleFromAccount, messageStylePayload, type MessageStyleDraft } from '../../messageStylePolicy';
import { Btn, PageFrame, SaveStatus, Section, SectionRail, Stack, type RailRow } from '../kit';
import { C } from '../tokens';
import { mergeStatus } from '../style';
import { EmojiField, FieldShell, TextField, TimezoneRow } from './Fields';
import { PersonaPicker, PersonaRow, UndoBanner } from './Persona';
import { MessageStyleCard } from './MessageStyle';
import { GROUPS, draftOf, isFilled, matchPreset, payloadOf, sectionFields, type Draft, type EmojiKey, type FieldKey, type Section as Sec, type TextKey } from './model';

interface Undo { ns: string; hs: string; ne: string[]; base?: ChatStylePreset; name: string }

export function PersonalityPage({ account, onAccountUpdate }: { account: Account; onAccountUpdate: (a: Account) => void }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tc } = useTranslation('chatAssistant');
  const id = account.account_id;
  const { getPermissions, isSuperAdmin } = useAuth();
  const canCopy = isAccountOwner(getPermissions(id)) || isSuperAdmin;

  const [params, setParams] = useSearchParams();
  const sec: Sec = params.get('s') === 'info' ? 'info' : 'style';
  const select = (s: string) => { const n = new URLSearchParams(params); n.set('s', s); setParams(n, { replace: true }); };

  // The page is keyed by account, so the account it mounts with is the saved state.
  const [draft, setDraft] = useState<Draft>(() => draftOf(account));
  const [style, setStyle] = useState<MessageStyleDraft>(() => messageStyleFromAccount(account));
  const [ready, setReady] = useState(true);
  const saved = useRef(payloadOf(draft));
  const [base, setBase] = useState<ChatStylePreset | undefined>(() => matchPreset(draft));
  const [undo, setUndo] = useState<Undo | null>(null);
  const [open, setOpen] = useState<Partial<Record<FieldKey, boolean>>>({});
  const [picker, setPicker] = useState(false);
  const [copy, setCopy] = useState<null | 'conversationalStyle' | 'modelContext'>(null);

  // Send only the columns that changed since the last save.
  const auto = useAutosave<Draft>({
    value: draft,
    enabled: ready,
    save: async (d) => {
      const next = payloadOf(d);
      const diff = Object.fromEntries(Object.entries(next).filter(([k, v]) => saved.current[k] !== v));
      if (!Object.keys(diff).length) return;
      const updated = await api.updateAccount(id, diff as Partial<Account>);
      saved.current = next;
      onAccountUpdate(updated);
    },
  });
  const styleAuto = useAutosave<MessageStyleDraft>({
    value: style,
    enabled: ready,
    validate: (v) => (Object.values(messageStyleErrors(v)).some(Boolean) ? t('save.invalid') : null),
    save: async (v) => { onAccountUpdate(await api.updateAccount(id, messageStylePayload(v))); },
  });
  const status = mergeStatus(auto, styleAuto);

  // Reload from the server (after "Copy from account"): nothing is saved while
  // the fresh values land, so they become the new baseline.
  const reload = async () => {
    const a = await api.getAccount(id);
    onAccountUpdate(a);
    const d = draftOf(a);
    setReady(false);
    setDraft(d);
    setStyle(messageStyleFromAccount(a));
    saved.current = payloadOf(d);
    setBase(matchPreset(d));
    setUndo(null);
  };
  useEffect(() => { if (!ready) setReady(true); }, [ready]);

  const setText = (k: TextKey, v: string) => setDraft((d) => ({ ...d, text: { ...d.text, [k]: v } }));
  const setEmoji = (k: EmojiKey, v: string[]) => setDraft((d) => ({ ...d, emoji: { ...d.emoji, [k]: v } }));
  const setNow = (fn: (d: Draft) => Draft) => { setDraft(fn); window.setTimeout(auto.flush, 0); };
  const setStyleNow = (v: MessageStyleDraft) => {
    const choice = v.start_letter_policy !== style.start_letter_policy || v.speed_policy !== style.speed_policy;
    setStyle(v);
    if (choice) window.setTimeout(styleAuto.flush, 0);
  };

  // The persona is whichever preset the two chat styles match; editing either
  // one makes it "Custom" (based on the preset last loaded).
  const current = useMemo(() => matchPreset(draft), [draft]);
  const usePreset = (p: ChatStylePreset) => {
    setUndo({ ns: draft.text.conversational_dynamics, hs: draft.text.hot_conversational_dynamics, ne: draft.emoji.normal_chat_emojis, base, name: tc(`modelInfo.stylePresets.${p.id}.name`) });
    setBase(p);
    setPicker(false);
    setNow((d) => ({
      ...d,
      text: { ...d.text, conversational_dynamics: p.normal, hot_conversational_dynamics: p.hot },
      emoji: { ...d.emoji, normal_chat_emojis: [...p.emojis] },
    }));
  };
  const undoPreset = () => {
    if (!undo) return;
    setBase(undo.base);
    setUndo(null);
    setNow((d) => ({
      ...d,
      text: { ...d.text, conversational_dynamics: undo.ns, hot_conversational_dynamics: undo.hs },
      emoji: { ...d.emoji, normal_chat_emojis: undo.ne },
    }));
  };

  const fields = sectionFields(sec);
  const anyOpen = fields.some((fd) => open[fd.key]);
  const toggleAll = () => setOpen((o) => ({ ...o, ...Object.fromEntries(fields.map((fd) => [fd.key, !anyOpen])) }));
  const count = (s: Sec) => { const fs = sectionFields(s); return { n: fs.filter((fd) => isFilled(draft, fd)).length, total: fs.length }; };
  const rail: RailRow[] = (['style', 'info'] as Sec[]).map((s) => {
    const c = count(s);
    return { id: s, label: t(`mp.s.${s}`), meta: `${c.n}/${c.total}`, metaTitle: t('mp.filledTip', c) };
  });

  const right = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
      <SaveStatus status={status.status} error={status.error} onRetry={status.retry} idleLabel />
      <Btn kind="text" onClick={toggleAll} style={{ color: C.purpleLt, padding: '0 8px' }}>{anyOpen ? t('mp.collapseAll') : t('mp.expandAll')}</Btn>
      {canCopy && (
        <Btn kind="muted" icon="copy" title={t('mp.copyTip')} onClick={() => setCopy(sec === 'style' ? 'conversationalStyle' : 'modelContext')} style={{ color: C.body }}>
          {t('mp.copy')}
        </Btn>
      )}
    </div>
  );

  return (
    <>
      <PageFrame
        rail={<SectionRail heading={t('mp.rail')} items={rail} active={sec} onSelect={select} />}
        headline={t(`mp.s.${sec}`)}
        desc={t(`mp.d.${sec}`)}
        right={right}
      >
        <Stack gap={18}>
          {sec === 'style' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <PersonaRow current={current} base={base} onOpen={() => setPicker(true)} />
              {undo && <UndoBanner label={t('mp.persona.loaded', { name: undo.name })} onUndo={undoPreset} onDismiss={() => setUndo(null)} />}
            </div>
          )}
          {GROUPS[sec].map((g) => (
            <Section key={g.id} title={t(`mp.g.${g.id}.t`)} hint={t(`mp.g.${g.id}.h`)}>
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
                {g.tz && <TimezoneRow value={draft.timezone} onChange={(v) => setNow((d) => ({ ...d, timezone: v }))} />}
                {g.fields.map((fd, j) => {
                  const o = !!open[fd.key];
                  const tog = () => setOpen((x) => ({ ...x, [fd.key]: !x[fd.key] }));
                  return (
                    <FieldShell key={fd.key} first={j === 0 && !g.tz} open={o}>
                      {fd.emoji
                        ? <EmojiField def={fd} value={draft.emoji[fd.key as EmojiKey]} onChange={(v) => setEmoji(fd.key as EmojiKey, v)} open={o} onToggle={tog} />
                        : <TextField def={fd} value={draft.text[fd.key as TextKey]} onChange={(v) => setText(fd.key as TextKey, v)} open={o} onToggle={tog} />}
                    </FieldShell>
                  );
                })}
              </div>
            </Section>
          ))}
          {sec === 'style' && <MessageStyleCard value={style} onChange={setStyleNow} />}
        </Stack>
      </PageFrame>

      {picker && <PersonaPicker current={current} custom={!current} onUse={usePreset} onClose={() => setPicker(false)} />}
      <CopyConfigModal
        type={copy ?? 'conversationalStyle'}
        currentAccountId={id}
        isOpen={copy !== null}
        onClose={() => setCopy(null)}
        onComplete={() => { void reload(); }}
      />
    </>
  );
}

